/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: TenantsActions.tsx
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Action buttons component for Tenants data grid.
────────────────────────────────────────────────────────────────*/

import { Edit, Copy, Trash2 } from 'lucide-react';
import { useI18n } from '../../../hooks/useI18n';
import SimpleTooltip from '../../../components/ui/SimpleTooltip';
import type { Tenant } from '../types';

interface TenantsActionsProps {
  tenant: Tenant;
  onEdit: (tenant: Tenant) => void;
  onDuplicate: (tenant: Tenant) => void;
  onDelete: (tenant: Tenant) => void;
}

export function TenantsActions({
  tenant,
  onEdit,
  onDuplicate,
  onDelete
}: TenantsActionsProps) {
  const { t } = useI18n();

  return (
    <div className="flex items-center gap-1.5">
      <SimpleTooltip content={t('tenants.editTenant')} position="top">
        <button
          onClick={() => onEdit(tenant)}
          className="p-2 text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20"
          type="button"
          aria-label={t('tenants.editTenant')}
        >
          <Edit className="h-4 w-4" />
        </button>
      </SimpleTooltip>
      <SimpleTooltip content={t('tenants.duplicateTenant')} position="top">
        <button
          onClick={() => onDuplicate(tenant)}
          className="p-2 text-green-600 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/20"
          type="button"
          aria-label={t('tenants.duplicateTenant')}
        >
          <Copy className="h-4 w-4" />
        </button>
      </SimpleTooltip>
      <SimpleTooltip
        content={tenant.isPrimary ? t('tenants.cannotDeletePrimary') : t('tenants.deleteTenant')}
        position="top"
      >
        <button
          onClick={tenant.isPrimary ? undefined : () => onDelete(tenant)}
          disabled={tenant.isPrimary}
          className={`p-2 transition-colors ${tenant.isPrimary
              ? 'text-gray-300 cursor-not-allowed dark:text-gray-600'
              : 'text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20'
            }`}
          type="button"
          aria-label={t('tenants.deleteTenant')}
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </SimpleTooltip>
    </div>
  );
}

